import React, { useEffect, useState } from "react";
import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Memory } from "@mui/icons-material";
import { findDeviceId, searchDashboards } from "../../endpoints/api";
import useStore from "../../State";

type DeviceChoiceProps = {
  handleBack: () => void;
  handleNext: (deviceId: string) => void;
};

const DeviceChoice: React.FC<DeviceChoiceProps> = ({
  handleBack,
  handleNext,
}) => {
  const { t } = useTranslation();

  const [devices, setDevices] = useState<{ name: string; id: string }[]>([]);
  const [deviceId, setDeviceId] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);

  const user = useStore((state: any) => state.user);
  useEffect(() => {
    searchDashboards(user?.name || "", user?.password || "").then(
      (res: any) => {
        if (res && res.length > 0) {
          for (let i = 0; i < res.length; i++) {
            if (
              res[i] &&
              res[i].id &&
              res[i].type === "dash-folder" &&
              res[i].title !== "Template"
            ) {
              const tmpId = res[i].id;
              searchDashboards(
                user?.name || "",
                user?.password || "",
                tmpId
              ).then((r: any) => {
                if (r && r.length > 0) {
                  for (let j = 0; j < r.length; j++) {
                    if (r[j] && r[j].uid && r[j].type === "dash-db") {
                      const tmpUid = r[j].uid;
                      const tmpTitle =
                        r[j].title && r[j].title.split(" - ").length >= 2
                          ? r[j].title.split(" - ")[1]
                          : r[j].title;
                      findDeviceId(tmpUid).then((r: any) => {
                        if (
                          r &&
                          r.dashboard &&
                          r.dashboard.templating &&
                          r.dashboard.templating.list &&
                          r.dashboard.templating.list.length > 0
                        ) {
                          const tmpElement: any = { name: tmpTitle, id: "" };
                          const assetIdItem = (
                            r.dashboard.templating.list as any[]
                          ).find((x: any) => x.name === "asset_id");

                          if (assetIdItem) {
                            tmpElement.id = assetIdItem.query;
                          }
                          if (tmpElement.id && tmpElement.name) {
                            setDevices((prev) => {
                              if (
                                prev.filter((f) => f.id === tmpElement.id)
                                  .length === 0
                              ) {
                                return [
                                  ...prev,
                                  {
                                    id: tmpElement.id,
                                    name: `${tmpElement.name} (${tmpElement.id})`,
                                  },
                                ];
                              } else {
                                return [...prev];
                              }
                            });
                          }
                          setLoading(false);
                        } else {
                          setLoading(false);
                        }
                      });
                    }
                  }
                }
              });
            }
          }
        } else {
          setLoading(false);
        }
      }
    );
  }, []);

  if (loading) {
    return (
      <Grid
        container
        spacing={2}
        direction="column"
        alignItems="center"
        justifyContent="center"
        textAlign="center"
      >
        <Grid item>
          <Memory style={{ fontSize: "64px" }} />
        </Grid>
        <Grid item>
          <Typography variant="h5">{t("selectDevice")}</Typography>
        </Grid>
        <Grid item>
          <Typography>{t("selectDeviceDescription")}</Typography>
        </Grid>
        <Grid
          container
          item
          spacing={4}
          alignItems="center"
          justifyContent="center"
          textAlign="left"
        >
          <Grid item xs={12} md={8} xl={6}>
            <LinearProgress />
          </Grid>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid
      container
      spacing={2}
      direction="column"
      alignItems="center"
      justifyContent="center"
      textAlign="center"
    >
      <Grid item>
        <Memory style={{ fontSize: "64px" }} />
      </Grid>
      <Grid item>
        <Typography variant="h5">{t("selectDevice")}</Typography>
      </Grid>
      <Grid item>
        <Typography>{t("selectDeviceDescription")}</Typography>
      </Grid>
      <Grid
        container
        item
        spacing={4}
        alignItems="center"
        justifyContent="center"
      >
        <Grid item xs={12} md={8} xl={6}>
          <Grid container spacing={1} direction="column">
            <Grid item>
              <FormControl fullWidth style={{ textAlign: "start" }}>
                <InputLabel id="select-label">{t("device")}</InputLabel>
                <Select
                  labelId="select-label"
                  value={deviceId}
                  label={t("device")}
                  onChange={(event: SelectChangeEvent) =>
                    setDeviceId(event.target.value as string)
                  }
                >
                  {devices.length > 0 &&
                    devices.map((f) => (
                      <MenuItem value={f.id} key={f.id}>
                        {f.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item>
              <Grid
                container
                spacing={2}
                display="flex"
                alignItems="flex-start"
                justifyContent="space-between"
              >
                <Grid item>
                  <Button
                    size="large"
                    variant="outlined"
                    style={{ float: "right", marginTop: "8px" }}
                    onClick={handleBack}
                  >
                    {t("back")}
                  </Button>
                </Grid>
                <Tooltip title={deviceId ? "" : `${t("fillInAllFields")}`}>
                  <Grid item>
                    <Button
                      data-testid="next"
                      disabled={!deviceId}
                      size="large"
                      variant="contained"
                      style={{ float: "right", marginTop: "8px" }}
                      onClick={() => handleNext(deviceId)}
                    >
                      {t("next")}
                    </Button>
                  </Grid>
                </Tooltip>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default DeviceChoice;
